/**
 * Scheduled-dashboard KPIs (live firm vs forecast).
 *
 * Backed by ``analytics/views.scheduled_dashboard`` — splits forward-dated PO
 * line schedules into a "firm" window (within each part's schedule_firm days)
 * and "forecast" (beyond it). Tenant default firm window applies when a part
 * has no PartScheduleEntry.
 */
import api, { type AxiosResponse } from './client';
import type { DeliveryMetricMode } from './types/delivery-metric-mode';

export interface ScheduledBucket {
  schedules: number;
  total_qty: number;
  open_qty: number;
  shipped_qty: number;
  total_value: number;
  open_value: number;
}

export interface QualityMetrics {
  ncrs: number;
  reject_qty: number;
  received_qty: number;
  ppm: number;
}

export interface DeliveryMetrics {
  // Schedule-line based OTIF/OTD (canonical).
  // OTIF = schedule lines fulfilled in-full within the part's firm window.
  // OTD  = schedule lines whose deliveries arrived within the firm window.
  /** null = N/A — nothing closed or in arrears to divide by (METRIC-DEFINITIONS Q13). */
  otif_pct: number | null;
  /** null = N/A on the PO Line Dashboard (CR-7312): nothing received in the period. */
  otd_pct: number | null;
  total_due: number;
  /** Where the arrears term came from: live stock, that month's frozen snapshot, or none captured. */
  arrears_source?: 'live' | 'snapshot' | 'none' | null;
  otif_met: number;
  otd_met: number;
  /** OTD denominator — total_due minus on-hold slots (excluded from OTD). */
  otd_due?: number;
  in_full: number;
  not_delivered: number;
  // OTIF Mix donut buckets. Pending excludes any schedule still inside its
  // late-tolerance window AND not yet delivered.
  passed: number;
  pending: number;
  failed: number;
  /** CR-6327: past due and still undelivered — never scored, and no longer
   *  counted as Failed. Optional because the Scheduled Dashboard still
   *  reports the older three-state mix, where Failed absorbs these; on that
   *  payload the donut falls back to no Arrears slice. */
  arrears?: number;
  // GRN-status counters — used for the OTD-mix donut breakdown only.
  on_time: number;
  early: number;
  late: number;
  total_with_status: number;
  total_grns: number;
  /** CR-7312 (PO Line Dashboard): every receipt in the OTD population, the OTD denominator. */
  total_otd?: number;
  /** M4 (wave 3b): GRNs in the OTD population with no verdict yet — they sit in
   *  the denominator only; older payloads omit the field. */
  unclassified?: number;
  appeals: number;
}

export interface CostMetrics {
  // Period-bound — driven by schedule lines whose schedule_date is in window.
  total_lines: number;        // committed lines this period
  open_lines: number;         // still outstanding
  total_value: number;        // schedule_qty × unit_price
  received_value: number;     // shipped_qty × unit_price (delivered against)
  open_value: number;         // balance_qty × unit_price (still owed)
  arrears_lines: number;      // past-due AND still open within the period
  arrears_value: number;
  spend: number;              // legacy GRN-receipt-window spend (per-week chart)
}

export interface DeliveryByDay {
  day: 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat';
  on_time: number;
  early: number;
  late: number;
  pending: number;
}

export interface PeriodData {
  window_start: string;
  window_end: string;
  quality: QualityMetrics;
  delivery: DeliveryMetrics;
  cost: CostMetrics;
}

export interface CurrentWeekData extends PeriodData {
  otif_window_start: string;
  otif_window_end: string;
  frozen: boolean;
  deliveries_by_day: DeliveryByDay[];
  // Week selector context (see ScheduledShell). selected_week_offset: 0 = live
  // week, -1 = last week, etc. is_live: true only for the in-progress week.
  selected_week_offset?: number;
  is_live?: boolean;
}

export interface WeekTrendPoint extends PeriodData {
  label: string;
  week_start: string;
  week_end: string;
  /**
   * Week marked as a shutdown in Admin Setup (Purchasing → OTIF shutdown
   * weeks). The figures are still populated — the tab drops the week from the
   * 12-week OTIF average and the trend line rather than scoring it as zero.
   * Scoped to this tab: YTD and supplier scorecards still count the week.
   */
  is_shutdown?: boolean;
}

export interface MonthTrendPoint extends PeriodData {
  label: string;
  month_start: string;
  month_end: string;
}

export interface PortfolioContext {
  // Point-in-time aggregates over forward (or past-due) real schedules.
  // These respect the active filter scope (customer, supplier, erp_order_type,
  // part_number) — they are NOT a global cross-tenant figure.
  open_orders_value: number;
  arrears_value: number;
}

/** One delivery week of forward pipeline, split firm vs forecast. */
export interface ForecastWeek {
  /** ISO date of the week start, honouring the tenant's week_start_day. */
  week_start: string;
  firm_qty: number;
  forecast_qty: number;
  firm_value: number;
  forecast_value: number;
  /**
   * Open qty that week keyed by supplier name. Same numbers as
   * firm_qty + forecast_qty, read along the supplier axis instead.
   */
  by_supplier: Record<string, number>;
}

/**
 * Current supplier + unit price for the searched part.
 *
 * Null unless a single part is filtered — a price or supplier across a mixed
 * basket of parts would be meaningless.
 */
export interface PartInfo {
  part_number: string;
  part_description: string;
  /** The supplier's own code for the part; null when they've given us none. */
  supplier_part_number: string | null;
  current_supplier: string | null;
  current_supplier_id: number | null;
  /** Price from the most recently issued PO carrying the part. */
  unit_price: number | null;
  currency: string | null;
  price_source_po: string | null;
  price_source_date: string | null;
  /** Other suppliers that have supplied the part (dual sourcing). */
  other_suppliers: string[];
}

/**
 * Forward position of the filtered supplier — the mirror of {@link PartInfo}
 * when the filter is a supplier rather than a part. Null unless a supplier is
 * filtered. Counts only forward, outstanding schedules.
 */
export interface SupplierInfo {
  supplier: string;
  /** Distinct parts with forward qty outstanding. */
  parts: number;
  open_qty: number;
  /** Plain sum of qty x unit_price — no FX conversion happens anywhere. */
  open_value: number;
  /**
   * Currency of open_value, or null when the supplier trades in more than one.
   * Null must not be formatted with a symbol: the total mixes currencies and
   * is not a real money figure.
   */
  currency: string | null;
}

/** One part a filtered supplier owes, with its price and qty due. */
export interface SupplierPart {
  part_number: string;
  part_description: string;
  /** The supplier's own code for the part; null when they've given us none. */
  supplier_part_number: string | null;
  open_qty: number;
  open_value: number;
  /** ISO date of the earliest forward schedule for this part. */
  next_due: string | null;
  /** Latest price for this (supplier, part) — matches the part strip. */
  unit_price: number | null;
  currency: string | null;
  /**
   * Firm-boundary days for the part. Null means no PartScheduleEntry exists,
   * so the part has no firm window and every future delivery is forecast.
   */
  firm_days: number | null;
}

export interface ScheduledDashboardData {
  customer_id: number;
  supplier_id: number | null;
  default_firm_days: number;
  supplier_info: SupplierInfo | null;
  /** Per-part breakdown behind supplier_info; empty unless a supplier is filtered. */
  supplier_parts: SupplierPart[];
  /** Forward open qty per delivery week; sums to the firm/forecast tiles. */
  forecast_weeks: ForecastWeek[];
  /**
   * Suppliers holding forward qty in scope, biggest first. A single entry
   * means there is nothing to split by supplier.
   */
  forecast_week_suppliers: string[];
  part_info: PartInfo | null;
  // Echoed back so the UI can reflect the active scope.
  erp_order_type: string | null;
  part_number: string | null;
  firm: ScheduledBucket;
  forecast: ScheduledBucket;
  today: string;
  // Customer-level setting that gates OTIF / OTD blocks across all
  // dashboard surfaces. Mirrors PerformanceSettings.delivery_metric_mode.
  delivery_metric_mode?: DeliveryMetricMode;
  // CR-6543 r2: tenant-configured ring/trend targets (SupplierPerformance-
  // Settings.otif_target / otd_target) — the UI previously hardcoded 95.
  otif_target?: number;
  otd_target?: number;
  // Arrears grace buffer (days) — drill-downs subtract this from the past-due
  // cutoff so the list matches the buffered tile count.
  arrears_buffer_days?: number;
  portfolio: PortfolioContext;
  current_week: CurrentWeekData;
  ytd: PeriodData;
  per_week: { weeks: WeekTrendPoint[] };
  per_month: { months: MonthTrendPoint[] };
}

export interface ScheduledDashboardParams {
  customer_id?: number;
  supplier_id?: number;
  default_firm_days?: number;
  // '4' = scheduling agreement (Scheduled Dashboards tab)
  // '1' = spot order (Spot Orders tab)
  erp_order_type?: string;
  /** Exact match on our part number OR the line supplier's own part number. */
  part_number?: string;
  // Shift the Current Week panel back N whole weeks (0 = live week, -1 = last
  // week). Only the current_week block of the response moves.
  week_offset?: number;
}

// ============================================================================
// Demand Stability — forward schedule movement (schedule-line installs)
// ============================================================================

/** One forward delivery week: current book + what changed vs baseline. */
export interface DemandForwardWeek {
  /** ISO Monday of the delivery week. */
  week: string;
  /** Scheduled qty currently due that week. */
  current: number;
  /** Qty added to that week since the baseline snapshot. */
  added: number;
  /** Qty removed from that week since the baseline snapshot. */
  removed: number;
}

export interface DemandForwardView {
  /** ISO date of the baseline snapshot the deltas compare against. */
  baseline: string;
  weeks: DemandForwardWeek[];
}

export interface DemandStabilityData {
  /** 'snapshot' when the baseline is a stored DemandSnapshot; 'reconstruction' before the first weekly capture exists. */
  source: 'snapshot' | 'reconstruction';
  /** ISO date the demand book was last captured; null before the first run. */
  last_captured: string | null;
  forward: DemandForwardView;
}

export interface DemandStabilityParams {
  customer_id?: number | string;
  supplier_id?: number | string;
  part_number?: string;
}

export const scheduledDashboardAPI = {
  get(params?: ScheduledDashboardParams): Promise<AxiosResponse<ScheduledDashboardData>> {
    return api.get('/analytics/scheduled-dashboard/', { params });
  },

  /** Demand Stability — weekly demand movement + per-part churn. */
  getDemandStability(
    params?: DemandStabilityParams,
  ): Promise<AxiosResponse<DemandStabilityData>> {
    return api.get('/analytics/demand-stability/', { params });
  },

  /**
   * Excel export of the forward schedule lines behind the Forecast tab.
   *
   * Pass `export_all` to ignore the supplier / part / order-type filters and
   * export the customer's whole forward pipeline; omit it to export exactly
   * what the filters match. Tenant scoping always applies.
   *
   * @param params - Same filter scope as {@link get}, plus `export_all`.
   * @returns The .xlsx blob.
   */
  exportForecast(
    params?: ScheduledDashboardParams & { export_all?: boolean },
  ): Promise<AxiosResponse<Blob>> {
    return api.get('/analytics/scheduled-dashboard/forecast-export/', {
      params,
      responseType: 'blob',
    });
  },
};

// ============================================================================
// CR-6310: Performance Scorecard for Dashboard Performance Tab
// ============================================================================

export interface PerformanceScorecardHeader {
  supplier_id: number | null;
  supplier_name: string;
  year: number;
  month: string;
  month_number: number;
  date_range: string;
  report_date: string;
}

export interface PerformanceScorecardLineOrders {
  delivered_ytd: number;
  expected_ytd: number;
  delivered_month: number;
  expected_month: number;
}

export interface PerformanceScorecardDelivery {
  otif_ytd: number | null;
  otif_month: number | null;
}

export interface PerformanceScorecardCost {
  value_received_ytd: number;
  value_received_month: number;
  currency: string;
}

export interface PerformanceScorecardReturns {
  rtvs_ytd: number;
  rtvs_month: number;
}

export interface PerformanceScorecardQuality {
  concessions_accepted_ytd: number;
  concessions_raised_month: number;
}

export interface PerformanceScorecardConcerns {
  ncrs_ytd: number;
  ncrs_month: number;
}

/** CR-6310: Change Request metrics by type and initiator */
export interface PerformanceScorecardChangeType {
  customer_ytd: number;
  supplier_ytd: number;
  customer_month: number;
  supplier_month: number;
}

export interface PerformanceScorecardChangeRequests {
  date_changes: PerformanceScorecardChangeType;
  price_changes: PerformanceScorecardChangeType;
  qty_changes: PerformanceScorecardChangeType;
}

export interface PerformanceScorecardRating {
  rating_ytd: string;
  statement_ytd: string;
  rating_ytd_color: string;
  rating_month: string;
  statement_month: string;
  rating_month_color: string;
  /** Customer review 11 Sep (msg 2): the configured label + palette hex of the
   *  same band, so the cards match the Supplier Performance table. Null hex
   *  for N/A or an unconfigured grade. Optional: older deploys omit them. */
  rating_ytd_label?: string;
  rating_ytd_hex?: string | null;
  rating_month_label?: string;
  rating_month_hex?: string | null;
}

export interface PerformanceScorecardDailyOTIF {
  date: string;
  otif_percent: number;
  target: number;
  total_lines: number;
  on_time: number;
  failed: number;
}

export interface PerformanceScorecardData {
  header: PerformanceScorecardHeader;
  line_orders: PerformanceScorecardLineOrders;
  delivery_performance: PerformanceScorecardDelivery;
  cost: PerformanceScorecardCost;
  returns: PerformanceScorecardReturns;
  quality: PerformanceScorecardQuality;
  supplier_concerns: PerformanceScorecardConcerns;
  change_requests: PerformanceScorecardChangeRequests;
  supplier_rating: PerformanceScorecardRating;
  daily_otif: PerformanceScorecardDailyOTIF[];
}

export interface PerformanceScorecardParams {
  /** Required for supplier-portal users — the customer whose scorecard is read.
   *  See analytics/scope_resolver.py: the supplier branch 403s without it. */
  customer_id?: number;
  supplier_id?: number;
  start_date?: string;
  end_date?: string;
  strategic_only?: boolean;
}

export const performanceScorecardAPI = {
  /**
   * Get the performance scorecard summary for the Dashboard Performance tab.
   *
   * @param params - Optional supplier_id to filter to a single supplier
   * @returns Scorecard with YTD and Month metrics for all categories
   */
  get(params?: PerformanceScorecardParams): Promise<AxiosResponse<PerformanceScorecardData>> {
    return api.get('/analytics/performance-scorecard/', { params });
  },
};

// ============================================================================
// CR-6381: Supplier Rating Distribution for Dashboard Performance Tab
// ============================================================================

export interface RatingDistributionPeriod {
  month: string;
  year: number;
}

export interface RatingDistributionData {
  /** Count of suppliers per rating band for the selected month */
  monthly: Record<string, number>;
  /** Count of suppliers per rating band for YTD */
  overall: Record<string, number>;
  /** Period information */
  period: RatingDistributionPeriod;
  /** Total unique suppliers across both periods */
  total_suppliers: number;
}

export interface RatingDrilldownSupplier {
  id: number;
  name: string;
  otif_percent: number | null;
  po_line_count: number;
  ncr_count: number;
  /** PPM (Parts Per Million) - quality metric */
  ppm: number;
  /** Rating score used for sorting (same as OTIF %) */
  rating_score: number | null;
}

export interface RatingDrilldownData {
  rating: string;
  period: 'monthly' | 'overall';
  suppliers: RatingDrilldownSupplier[];
  count: number;
}

export interface RatingDistributionParams {
  supplier_id?: number;
  start_date?: string;
  end_date?: string;
  strategic_only?: boolean;
}

export interface RatingDrilldownParams extends RatingDistributionParams {
  period?: 'monthly' | 'overall';
}

export const supplierRatingDistributionAPI = {
  /**
   * Get the distribution of supplier ratings across rating bands.
   *
   * @param params - Date range and strategic filter
   * @returns Distribution counts for monthly and YTD periods
   */
  get(params?: RatingDistributionParams): Promise<AxiosResponse<RatingDistributionData>> {
    return api.get('/analytics/supplier-rating-distribution/', { params });
  },

  /**
   * Get suppliers that fall into a specific rating band.
   *
   * @param rating - The rating grade (A, B, C, D, F, N/A)
   * @param params - Date range, strategic filter, and period type
   * @returns List of suppliers with that rating
   */
  getDrilldown(
    rating: string,
    params?: RatingDrilldownParams,
  ): Promise<AxiosResponse<RatingDrilldownData>> {
    return api.get(`/analytics/supplier-rating-distribution/${encodeURIComponent(rating)}/suppliers/`, {
      params,
    });
  },
};

// ============================================================================
// CR-6381: Change Requests Monthly for Performance Ratings Tab
// ============================================================================

export interface ChangeRequestMonthlyPoint {
  month: string;
  customer: number;
  supplier: number;
}

export interface ChangeRequestsMonthlyPeriod {
  start_month: string;
  end_month: string;
  year: number;
}

export interface ChangeRequestsMonthlyData {
  date_changes: ChangeRequestMonthlyPoint[];
  price_changes: ChangeRequestMonthlyPoint[];
  qty_changes: ChangeRequestMonthlyPoint[];
  period: ChangeRequestsMonthlyPeriod;
}

export interface ChangeRequestsMonthlyParams {
  supplier_id?: number;
  start_date?: string;
  end_date?: string;
  strategic_only?: boolean;
}

export const changeRequestsMonthlyAPI = {
  /**
   * Get monthly change request counts by type and initiator.
   *
   * @param params - Date range and strategic filter
   * @returns Monthly time series data for change request charts
   */
  get(params?: ChangeRequestsMonthlyParams): Promise<AxiosResponse<ChangeRequestsMonthlyData>> {
    return api.get('/analytics/change-requests-monthly/', { params });
  },
};

// ============================================================================
// CR-6381: Change Requests Drilldown
// ============================================================================

export interface ChangeRequestDrilldownRecord {
  id: number;
  po_number: string;
  line_number: string;
  part_number: string;
  supplier_name: string;
  field_changed: string;
  old_value: string;
  new_value: string;
  initiator: 'customer' | 'supplier';
  change_date: string;
  changed_by: string;
}

export interface ChangeRequestDrilldownData {
  month: string;
  change_type: string;
  initiator: string | null;
  records: ChangeRequestDrilldownRecord[];
  count: number;
}

export interface ChangeRequestDrilldownParams {
  month: string;
  change_type: 'date' | 'price' | 'qty';
  initiator?: 'customer' | 'supplier';
  supplier_id?: number;
  strategic_only?: boolean;
}

export const changeRequestsDrilldownAPI = {
  /**
   * Get change request details for drilldown.
   *
   * @param params - Month, change type, and optional filters
   * @returns List of change request records
   */
  get(params: ChangeRequestDrilldownParams): Promise<AxiosResponse<ChangeRequestDrilldownData>> {
    return api.get('/analytics/change-requests-drilldown/', { params });
  },
};
